import { FormEvent, useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import { ApplicationsList } from "../components/ApplicationsList";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Flex } from "../components/Flex";
import { Input } from "../components/Input";
import Topbar from "../components/Topbar";
import { FaPlus } from "react-icons/fa";
import { MdTune } from "react-icons/md";
import { Filters } from "../components/Filters";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";
import { useMandateStatuses } from "../context/MandateStatusContext";
import { capitalise } from "../utils/string";
import { Loader } from "../components/Loader";
import { UserFilter } from "../components/UserFilter";
import { useUsers } from "../data/useUsers";
import { useFilterOptions } from "../components/Filters/useFilterOptions";
import { env } from "../utils/env";
import { Box } from "../components/Box";
import { H3 } from "../components/Heading";
import { ApplicationsTable } from "../components/ApplicationsTable";
import { useBanner } from "../data/useBanner";
import BannerComponent from "../components/Banner";
import CustomTabs from "../components/CustomTabs";
import {
  HiOutlineSquares2X2,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
  HiOutlineViewColumns,
  HiOutlineTableCells,
} from "react-icons/hi2";
import { ViewToggle } from "../components/ViewToggle";
import { useBillingInfo } from "../data/useBillingInfo";
import { useAuth } from "../context/AuthContext";
import { useDashboardView } from "../hooks/useDashboardView";
import { applicationsView, APPLICATIONS_STATIC_OPTION_LISTS } from "../hooks/dashboardViews/applications";
import { ActiveFilterBar, getAppliedFilterCount } from "../components/ActiveFilterBar";

export const Columns = styled.div<any>`
  ${({ theme: { space }, submittedSearchTerm }) => `
    display: flex;
    gap: ${space[2]};
    padding: ${space[4]};
    overflow: auto;
  `}
`;

const Applications = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { statuses, isLoadingStatuses } = useMandateStatuses();
  const { data: bannerData, isLoading: isBannerLoading } = useBanner(true);
  const { data: billingData, isLoading: isLoadingBillingInfo } = useBillingInfo();

  const profilesEnabled = !!billingData?.data?.results?.[0]?.profiles_enabled;

  const { data: users } = useUsers();
  const options = useFilterOptions();

  const userOptions = users?.data?.results.map(
    ({ first_name, last_name, id, colour }) => ({
      value: id,
      label: `${first_name} ${last_name}`,
      firstName: first_name,
      lastName: last_name,
      colour,
    }),
  );

  const optionLists = useMemo(
    () => ({
      statuses: options.statusList ?? [],
      consultants: options.consultants ?? [],
      applicationTypes: options.mandateTypes ?? [],
      users: userOptions ?? [],
      profilesEnabled: [profilesEnabled],
      ...APPLICATIONS_STATIC_OPTION_LISTS,
    }),
    [options.statusList, options.consultants, options.mandateTypes, userOptions, profilesEnabled],
  );

  const optionListsReady = Boolean(
    options.statusList &&
      options.consultants &&
      options.mandateTypes &&
      users?.data &&
      !isLoadingBillingInfo,
  );

  const {
    view,
    setValue,
    setValues,
    clearValue,
    clearAll,
    appliedKeys,
    isReady: isViewReady,
  } = useDashboardView(applicationsView, { optionLists, optionListsReady });

  // Safety net alongside the schema's own validate() for the tab field
  // (tech spec 4.4): validate() corrects a restored tab of 2 on a tenant
  // without Profiles before the first render; this covers profilesEnabled
  // changing later in the session.
  useEffect(() => {
    if (isViewReady && !profilesEnabled && view.tab > 1) {
      setValue("tab", 0);
    }
  }, [isViewReady, profilesEnabled, view.tab, setValue]);

  const [searchTerm, setSearchTerm] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const isLoading = isLoadingStatuses;
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const isCardView = view.isCardView as boolean;
  const isTableView = isCardView === false;
  const activeTab = view.tab as number;
  const submittedSearchTerm = view.search as string;

  const selectedUser = useMemo(
    () => userOptions?.find((option) => option.value === view.assignedUser) ?? null,
    [userOptions, view.assignedUser],
  );

  const filters = useMemo(
    () => ({
      status: view.status,
      consultant: view.consultant,
      dateStarted: [view.dateStartedFrom, view.dateStartedTo],
      applicationType: view.applicationType,
      // The control for this is commented out in Filters/index.tsx and is
      // explicitly out of scope for restoration (tech spec 1.4).
      noOfIndividuals: null,
      includeArchived: view.includeArchived,
    }),
    [
      view.status,
      view.consultant,
      view.dateStartedFrom,
      view.dateStartedTo,
      view.applicationType,
      view.includeArchived,
    ],
  );

  const appliedFilterCount = useMemo(
    () => getAppliedFilterCount(appliedKeys, applicationsView),
    [appliedKeys],
  );

  const handleTabChange = (index) => {
    setValue("tab", index);
  };

  useEffect(() => {
    const dismissed = localStorage.getItem("bannerDismissed");
    if (dismissed || bannerData?.length === 0) {
      setIsBannerVisible(false);
    }

    const handleStorageChange = (event) => {
      if (event.key === "bannerDismissed" && event.newValue === "true") {
        setIsBannerVisible(false);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [bannerData]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [bannerHeight, setBannerHeight] = useState(0);
  const activeBannerRef = useRef<HTMLDivElement>(null);
  const activeBannerType = bannerData?.[activeIndex]?.banner_type;
  const isSmallBanner = activeBannerType !== "large";

  useEffect(() => {
    const updateHeight = () => {
      if (!activeBannerRef.current) return;

      requestAnimationFrame(() => {
        const height =
          activeBannerRef.current?.getBoundingClientRect().height || 0;

        setBannerHeight(height);

        document.documentElement.style.setProperty(
          "--banner-height",
          `${height}px`,
        );
      });
    };

    setTimeout(updateHeight, 100);

    const resizeObserver = new ResizeObserver(updateHeight);
    if (activeBannerRef.current) {
      resizeObserver.observe(activeBannerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [activeIndex, isBannerVisible, submittedSearchTerm]);

  // Chips add a row of vertical space between the tabs and the table/board
  // content that the height budget below doesn't otherwise know about; left
  // unaccounted for, the table area (and the fixed-position pagination bar
  // beneath it) overlaps the chip row instead of shrinking to make room.
  const hasFilterChips = appliedKeys.some((key) => applicationsView.fields[key]?.chipLabel);
  const filterChipsHeight = hasFilterChips ? 56 : 0;

  const columnHeight = isBannerVisible
    ? `calc(100vh - ${bannerHeight > 0 ? bannerHeight : 260}px - ${
        submittedSearchTerm
          ? isSmallBanner
            ? "350px"
            : "430px"
          : isSmallBanner
          ? "290px"
          : "370px"
      } - ${filterChipsHeight}px)`
    : `calc(100vh - ${submittedSearchTerm ? "355px" : "300px"} - ${filterChipsHeight}px)`;

  const handleDismiss = () => {
    localStorage.setItem("bannerDismissed", "true");
    setIsBannerVisible(false);
  };

  const filterUserIds = useMemo(
    () => [selectedUser?.value],
    [selectedUser?.value],
  );

  const handleFilterSubmit = (values) => {
    // One setValues call: one address-bar replacement, one storage write,
    // instead of one per panel field.
    setValues({
      status: values.status,
      consultant: values.consultant,
      dateStartedFrom: values.dateStarted?.[0] ?? null,
      dateStartedTo: values.dateStarted?.[1] ?? null,
      applicationType: values.applicationType,
      includeArchived: values.includeArchived,
    });
    setIsFiltersOpen(false);
  };

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setValue("search", searchTerm);
  };

  const handleResetSearch = () => {
    setSearchTerm("");
    setValue("search", "");
  };

  const currentType =
    activeTab === 0
      ? ""
      : activeTab === 1
      ? "individual"
      : profilesEnabled
      ? "entity"
      : "";

  // Rendered inside each tab's own content, directly above the table/board,
  // so it sits between the tab bar and the table - not after the table and
  // its pagination, which is both the wrong order and (now pagination is
  // in normal flow, not fixed) would push it below the fold.
  const filterBar = (
    <Box px="24px">
      <ActiveFilterBar
        appliedKeys={appliedKeys}
        schema={applicationsView}
        view={view}
        optionLists={optionLists}
        onRemove={clearValue}
        onClearAll={clearAll}
      />
    </Box>
  );

  const tableViewContent = (
    <div className="p-2">
      {filterBar}
      <ApplicationsTable
        searchTerm={submittedSearchTerm}
        filterUserIds={filterUserIds}
        filters={filters}
        bannerHeight={bannerHeight}
        isBannerVisible={isBannerVisible}
        isSmallBanner={isSmallBanner}
        hasFilterChips={hasFilterChips}
        _type={currentType}
        initialSorting={[{ id: view.sortBy as string, desc: view.sortDesc as boolean }]}
        initialPageSize={view.pageSize as number}
        onSortChange={(sorting) =>
          setValues({
            sortBy: sorting[0]?.id ?? "created_on",
            sortDesc: sorting[0]?.desc ?? true,
          })
        }
        onPageSizeChange={(pageSize) => setValue("pageSize", pageSize)}
      />
    </div>
  );

  const cardViewContent = (
    <>
      {filterBar}
      {!isLoading && statuses && (
        <Columns
          submittedSearchTerm={submittedSearchTerm}
          style={{ height: columnHeight }}
        >
          {statuses?.data.map((status) => (
            <ApplicationsList
              key={status.id}
              statusId={status.id}
              name={capitalise(status.status)}
              statusName={status.status}
              searchTerm={submittedSearchTerm}
              filterUserIds={filterUserIds}
              filters={filters}
              _type={currentType}
            />
          ))}
        </Columns>
      )}
    </>
  );

  const tabs = [
    {
      label: (
        <>
          <HiOutlineSquares2X2 size={16} />
          All
        </>
      ),
      content: isCardView ? cardViewContent : tableViewContent,
    },
    {
      label: (
        <>
          <HiOutlineUser size={16} />
          Individual
        </>
      ),
      content: isCardView ? cardViewContent : tableViewContent,
    },
    ...(profilesEnabled
      ? [
          {
            label: (
              <>
                <HiOutlineBuildingOffice2 size={16} />
                Entity
              </>
            ),
            content: isCardView ? cardViewContent : tableViewContent,
          },
        ]
      : []),
  ];

  return (
    <div>
      <Topbar title="Applications" noBorder>
        <Flex justifyContent="space-between">
          {env.isSearchApplicationsFeatureOn ? (
            <Flex as="form" ml={4} onSubmit={handleSearchSubmit}>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                type="search"
                size="small"
                placeholder="Search"
                maxWidth={250}
                mr={1}
              />
              <Button
                isDisabled={submittedSearchTerm === searchTerm}
                type="submit"
              >
                Search
              </Button>

              {submittedSearchTerm && (
                <Button
                  variant="text"
                  pl="0px"
                  ml={1}
                  onClick={handleResetSearch}
                >
                  Reset search
                </Button>
              )}
            </Flex>
          ) : (
            <Box />
          )}

          <Flex>
            <UserFilter
              size="regular"
              options={userOptions}
              value={selectedUser as any}
              onChange={(option: any) => setValue("assignedUser", option?.value ?? null)}
              mr={1}
            />
            {env.isFilterApplicationsFeatureOn && (
              <Button
                variant="secondary"
                mr={1}
                Icon={MdTune}
                iconPosition="right"
                onClick={() => setIsFiltersOpen((isOpen) => !isOpen)}
              >
                Filter
                {appliedFilterCount > 0 && <Badge ml={1}>{appliedFilterCount}</Badge>}
              </Button>
            )}
            <Button
              Icon={FaPlus}
              iconSize="16px"
              onClick={() => navigate(ROUTE.APPLICATION_NEW)}
              isDisabled={!hasPermission("mandate.create")}
              title={
                hasPermission("mandate.create")
                  ? undefined
                  : "You don't have permission to create applications"
              }
            >
              New Application
            </Button>
          </Flex>
        </Flex>
      </Topbar>

      <div>
        {isLoading && (
          <Flex justifyContent="center" alignItems="center" minHeight="25vh">
            <Loader />
          </Flex>
        )}

        {submittedSearchTerm && (
          <H3 m={4} mb="0px" color="gray.60">
            Showing applications for search term: "{submittedSearchTerm}"
          </H3>
        )}

        {!isBannerLoading &&
          bannerData &&
          bannerData.length > 0 &&
          isBannerVisible && (
            <BannerComponent
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              activeBannerRef={activeBannerRef}
              banners={bannerData}
              onDismiss={handleDismiss}
            />
          )}

        <CustomTabs
          tabs={tabs}
          customMarginLeft="24px"
          activeTab={activeTab}
          onTabChange={handleTabChange}
          variant="segmented"
          rightSlot={
            <ViewToggle
              value={isCardView ? "board" : "list"}
              onChange={(toggleValue) => setValue("isCardView", toggleValue === "board")}
              options={[
                {
                  value: "board",
                  label: "Board view",
                  icon: <HiOutlineViewColumns />,
                },
                {
                  value: "list",
                  label: "List view",
                  icon: <HiOutlineTableCells />,
                },
              ]}
            />
          }
        />

        <Filters
          isOpen={isFiltersOpen}
          onRequestClose={() => setIsFiltersOpen(false)}
          onSubmit={handleFilterSubmit}
          filters={filters}
          isTableView={isTableView}
        />
      </div>
    </div>
  );
};

export default Applications;
